/**
 * useWhatsAppCampaigns Hook
 *
 * Manages WhatsApp campaign state: segments + templates loading,
 * form data, variable validation, and API calls.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { whatsappApi } from "../api";
import type {
  SegmentSummary,
  WhatsAppTemplate,
  WhatsAppCampaignFormData,
} from "../types";
import type { DynamicFieldKey } from "../components/PersonalizationToggle";

export type WinnerCriterion = "open_rate" | "reply_rate" | "ctr";

const INITIAL_FORM: WhatsAppCampaignFormData = {
  segmentId: "",
  templateId: "",
  bodyVariables: {},
  headerVariable: undefined,
  headerMediaUrl: undefined,
};

export function useWhatsAppCampaigns(brandId: string) {
  /* ─── Segments ─── */
  const [segments, setSegments] = useState<SegmentSummary[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);

  /* ─── Templates ─── */
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  /* ─── Form ─── */
  const [form, setForm] = useState<WhatsAppCampaignFormData>(INITIAL_FORM);

  /** Per-variable dynamic field mapping: { "1": "firstName", "2": null } */
  const [dynamicFields, setDynamicFields] = useState<
    Record<string, DynamicFieldKey | null>
  >({});

  /* ─── UI state ─── */
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /* ─── A/B testing state ─── */
  const [abEnabled, setAbEnabled] = useState(false);
  const [variantBTemplateId, setVariantBTemplateId] = useState<string>("");
  const [splitPct, setSplitPct] = useState<number>(50);
  const [winnerCriterion, setWinnerCriterion] =
    useState<WinnerCriterion>("reply_rate");

  /* ─── Load segments + templates on mount ─── */
  useEffect(() => {
    let alive = true;

    (async () => {
      setSegmentsLoading(true);
      try {
        const res = await whatsappApi.getSegments(brandId);
        if (alive && res.success) setSegments(res.data);
      } catch (err) {
        console.error("Error loading segments:", err);
      } finally {
        if (alive) setSegmentsLoading(false);
      }
    })();

    (async () => {
      setTemplatesLoading(true);
      try {
        const res = await whatsappApi.getTemplates(brandId);
        if (alive && res.success) setTemplates(res.data);
      } catch (err) {
        console.error("Error loading templates:", err);
      } finally {
        if (alive) setTemplatesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [brandId]);

  /* ─── Derived: selected segment + template ─── */
  const selectedSegment = useMemo(
    () => segments.find((s) => s.id === form.segmentId) || null,
    [segments, form.segmentId],
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === form.templateId) || null,
    [templates, form.templateId],
  );

  const variantBTemplate = useMemo(
    () => templates.find((t) => t.id === variantBTemplateId) || null,
    [templates, variantBTemplateId],
  );

  /** Append an AI-generated template to the available list */
  const addTemplate = useCallback((template: WhatsAppTemplate) => {
    setTemplates((prev) => [template, ...prev]);
  }, []);

  /* ─── Form validation ─── */
  const isValid = useMemo(() => {
    if (!form.segmentId) return false;
    if (!form.templateId) return false;
    if (!selectedTemplate) return false;
    if (selectedTemplate.approvalStatus !== "approved") return false;

    // All body variables must be filled — either with a static value OR
    // mapped to a dynamic user field
    for (let i = 1; i <= selectedTemplate.variableCount; i++) {
      const hasDynamic = !!dynamicFields[String(i)];
      const hasStatic = !!form.bodyVariables[String(i)]?.trim();
      if (!hasDynamic && !hasStatic) return false;
    }

    // Header variable required if header has one
    if (
      selectedTemplate.header?.variableCount &&
      selectedTemplate.header.variableCount > 0 &&
      !form.headerVariable?.trim()
    ) {
      return false;
    }

    // Header media required if header type is media
    if (
      selectedTemplate.header?.type === "image" ||
      selectedTemplate.header?.type === "video" ||
      selectedTemplate.header?.type === "document"
    ) {
      if (!form.headerMediaUrl) return false;
    }

    return true;
  }, [form, selectedTemplate, dynamicFields]);

  /** Set or clear the dynamic field mapping for a variable */
  const setDynamicField = useCallback(
    (index: number, field: DynamicFieldKey | null) => {
      setDynamicFields((prev) => ({ ...prev, [String(index)]: field }));
    },
    [],
  );

  /* ─── Estimated cost (segment size × template rate) ─── */
  const estimatedCost = useMemo(() => {
    if (!selectedSegment || !selectedTemplate) return 0;
    return selectedSegment.lastKnownCount * selectedTemplate.estimatedCostUSD;
  }, [selectedSegment, selectedTemplate]);

  /* ─── Update form ─── */
  const updateForm = useCallback(
    (updates: Partial<WhatsAppCampaignFormData>) => {
      setForm((prev) => ({ ...prev, ...updates }));
      setError(null);
      setSuccess(false);
    },
    [],
  );

  /** Reset bodyVariables + dynamicFields when template changes */
  const selectTemplate = useCallback((templateId: string) => {
    setForm((prev) => ({
      ...prev,
      templateId,
      bodyVariables: {},
      headerVariable: undefined,
      headerMediaUrl: undefined,
    }));
    setDynamicFields({});
    setError(null);
    setSuccess(false);
  }, []);

  /** Update a single body variable */
  const setBodyVariable = useCallback((index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      bodyVariables: { ...prev.bodyVariables, [String(index)]: value },
    }));
    setError(null);
  }, []);

  /* ─── Reset everything ─── */
  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM);
    setDynamicFields({});
    setError(null);
    setSuccess(false);
  }, []);

  /* ─── Send now ─── */
  const sendCampaign = useCallback(async () => {
    if (!isValid) return false;

    setSending(true);
    setError(null);
    try {
      const createRes = await whatsappApi.create({
        brandId,
        segmentId: form.segmentId,
        templateId: form.templateId,
        bodyVariables: form.bodyVariables,
        headerVariable: form.headerVariable,
        headerMediaUrl: form.headerMediaUrl,
      });

      if (!createRes.success) {
        setError(createRes.error || "Error al crear la campaña");
        return false;
      }

      const sendRes = await whatsappApi.send(createRes.data.id);
      if (!sendRes.success) {
        setError(sendRes.error || "Error al enviar");
        return false;
      }

      setSuccess(true);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Error inesperado");
      return false;
    } finally {
      setSending(false);
    }
  }, [brandId, form, isValid]);

  /* ─── Schedule for later ─── */
  const scheduleCampaign = useCallback(
    async (scheduledAt: string) => {
      if (!isValid) return false;

      setSending(true);
      setError(null);
      try {
        const createRes = await whatsappApi.create({
          brandId,
          segmentId: form.segmentId,
          templateId: form.templateId,
          bodyVariables: form.bodyVariables,
          headerVariable: form.headerVariable,
          headerMediaUrl: form.headerMediaUrl,
        });

        if (!createRes.success) {
          setError(createRes.error || "Error al crear");
          return false;
        }

        const scheduleRes = await whatsappApi.schedule(
          createRes.data.id,
          scheduledAt,
        );
        if (!scheduleRes.success) {
          setError(scheduleRes.error || "Error al programar");
          return false;
        }

        setSuccess(true);
        return true;
      } catch (err: any) {
        setError(
          err?.response?.data?.error || err.message || "Error inesperado",
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [brandId, form, isValid],
  );

  return {
    // Data
    segments,
    segmentsLoading,
    templates,
    templatesLoading,
    selectedSegment,
    selectedTemplate,
    addTemplate,

    // Form
    form,
    updateForm,
    selectTemplate,
    setBodyVariable,
    dynamicFields,
    setDynamicField,
    resetForm,
    isValid,
    estimatedCost,

    // A/B testing
    abEnabled,
    setAbEnabled,
    variantBTemplate,
    setVariantBTemplateId,
    splitPct,
    setSplitPct,
    winnerCriterion,
    setWinnerCriterion,

    // Actions
    sendCampaign,
    scheduleCampaign,
    sending,
    error,
    success,
  };
}
